import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, RotateCcw, Unlock } from 'lucide-react';
import { prendreCompte } from '@/shared/auth/auth.demo';
import type { Utilisateur } from '@/shared/auth/types';
import { lireEtat, reinitialiser } from '@/shared/donnees/demo/magasin';
import { Avatar } from '@/shared/ui';
import { libelleRole } from './libelles';

/**
 * L'accès libre, dans l'en-tête : prendre n'importe quel compte sans mot de
 * passe, ou remettre les données de départ. N'existe qu'en mode démo.
 */
export function SelecteurCompte({ utilisateur }: { utilisateur: Utilisateur }) {
  const [ouvert, setOuvert] = useState(false);
  const cadre = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ouvert) return undefined;
    function dehors(evenement: MouseEvent) {
      if (!cadre.current?.contains(evenement.target as Node)) setOuvert(false);
    }
    function echap(evenement: KeyboardEvent) {
      if (evenement.key === 'Escape') setOuvert(false);
    }
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => {
      document.removeEventListener('mousedown', dehors);
      document.removeEventListener('keydown', echap);
    };
  }, [ouvert]);

  function prendre(compte: Utilisateur) {
    setOuvert(false);
    prendreCompte(compte.id);
    void navigate('/');
  }

  return (
    <div ref={cadre} className="relative">
      <button
        type="button"
        onClick={() => {
          setOuvert((o) => !o);
        }}
        aria-expanded={ouvert}
        className="flex h-[38px] items-center gap-2 rounded-[10px] border border-note-border bg-note-bg pr-2 pl-2.5 text-[13px] font-medium text-note"
      >
        <Unlock className="size-4" aria-hidden />
        <span className="hidden xl:inline">Accès libre</span>
        <span className="hidden max-w-40 truncate sm:inline">
          · {utilisateur.nom || utilisateur.email}
        </span>
        <ChevronDown className="size-4" aria-hidden />
      </button>

      {ouvert ? (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
          <p className="border-b border-border-subtle px-4 py-3 text-xs text-muted">
            Données de démonstration, gardées dans ce navigateur. Choisissez qui vous êtes :
          </p>
          <ul className="max-h-80 overflow-y-auto p-2">
            {lireEtat().comptes.map((compte) => (
              <li key={compte.id}>
                <button
                  type="button"
                  onClick={() => {
                    prendre(compte);
                  }}
                  className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left hover:bg-surface-hover"
                >
                  <Avatar
                    nom={compte.nom || compte.email}
                    photo={compte.photo}
                    className="size-8 text-xs"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">
                      {compte.nom || compte.email}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {libelleRole(compte)}
                      {compte.actif ? '' : ' · suspendu'}
                    </span>
                  </span>
                  {compte.id === utilisateur.id ? (
                    <Check className="size-4 text-primary" aria-label="Compte actuel" />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setOuvert(false);
              reinitialiser();
              prendreCompte('u-bureau');
              void navigate('/');
            }}
            className="flex w-full items-center gap-2 border-t border-border-subtle px-4 py-3 text-[13px] font-medium text-muted hover:bg-surface-hover hover:text-ink"
          >
            <RotateCcw className="size-4" aria-hidden />
            Remettre les données de départ
          </button>
        </div>
      ) : null}
    </div>
  );
}
